// src/store/slices/branchInstallmentSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  initializeInstallmentConfigThunk,
  getInstallmentConfigThunk,
  toggleInstallmentsThunk,
  updateInterestConfigThunk,
  updatePaymentRulesThunk,
  updatePausePolicyThunk,
  updateKYCRequirementsThunk,
  getInstallmentPlansThunk,
  createInstallmentPlanThunk,
  updateInstallmentPlanThunk,
  deleteInstallmentPlanThunk,
  getInstallmentAnalyticsThunk,
  clearInstallmentError,
  resetInstallmentConfig,
  setSelectedPlan,
  clearSelectedPlan,
  setConfigEditMode,
  setPlanEditMode,
  clearAnalytics,
} from "../thunks/branchInstallmentThunks";

const initialState = {
  // Configuration data
  config: null,
  configByBranch: {}, // Store config for multiple branches

  // Plans data
  plans: [],
  plansByBranch: {}, // Store plans for multiple branches
  selectedPlan: null,

  // Analytics data
  analytics: null,
  analyticsByBranch: {},

  // Loading states
  loadingConfig: false,
  loadingPlans: false,
  loadingAnalytics: false,
  savingConfig: false,
  savingPlan: false,

  // Edit modes
  isEditingConfig: false,
  isEditingPlan: false,

  // Error handling
  error: null,
  planError: null,
  configError: null,
  analyticsError: null,

  // Validation
  validationErrors: [],

  // Current branch context
  currentBranchCode: null,
};

const branchInstallmentSlice = createSlice({
  name: "branchInstallment",
  initialState,
  reducers: {
    setCurrentBranchCode: (state, action) => {
      state.currentBranchCode = action.payload;
      // Load config and plans for this branch if available
      if (state.configByBranch[action.payload]) {
        state.config = state.configByBranch[action.payload];
      }
      if (state.plansByBranch[action.payload]) {
        state.plans = state.plansByBranch[action.payload];
      }
    },
    clearCurrentBranch: (state) => {
      state.currentBranchCode = null;
      state.config = null;
      state.plans = [];
      state.selectedPlan = null;
      state.analytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== CLEAR ACTIONS ==========
      .addCase(clearInstallmentError, (state) => {
        state.error = null;
        state.planError = null;
        state.configError = null;
        state.analyticsError = null;
        state.validationErrors = [];
      })
      .addCase(resetInstallmentConfig, (state) => {
        state.config = null;
        state.plans = [];
        state.selectedPlan = null;
        state.analytics = null;
        state.error = null;
        state.isEditingConfig = false;
        state.isEditingPlan = false;
      })
      .addCase(setSelectedPlan, (state, action) => {
        state.selectedPlan = action.payload;
      })
      .addCase(clearSelectedPlan, (state) => {
        state.selectedPlan = null;
        state.isEditingPlan = false;
      })
      .addCase(setConfigEditMode, (state, action) => {
        state.isEditingConfig = action.payload;
      })
      .addCase(setPlanEditMode, (state, action) => {
        state.isEditingPlan = action.payload;
      })
      .addCase(clearAnalytics, (state) => {
        state.analytics = null;
        state.analyticsError = null;
      })

      // ========== INITIALIZE CONFIG ==========
      .addCase(initializeInstallmentConfigThunk.pending, (state) => {
        state.loadingConfig = true;
        state.configError = null;
      })
      .addCase(initializeInstallmentConfigThunk.fulfilled, (state, action) => {
        state.loadingConfig = false;
        const { branchCode, data } = action.payload;
        state.config = data.data;
        state.configByBranch[branchCode] = data.data;
        state.currentBranchCode = branchCode;
      })
      .addCase(initializeInstallmentConfigThunk.rejected, (state, action) => {
        state.loadingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== GET CONFIG ==========
      .addCase(getInstallmentConfigThunk.pending, (state) => {
        state.loadingConfig = true;
        state.configError = null;
      })
      .addCase(getInstallmentConfigThunk.fulfilled, (state, action) => {
        state.loadingConfig = false;
        const { branchCode, data } = action.payload;
        state.config = data.data?.installmentConfig || data.data;
        state.configByBranch[branchCode] =
          data.data?.installmentConfig || data.data;
        state.currentBranchCode = branchCode;
      })
      .addCase(getInstallmentConfigThunk.rejected, (state, action) => {
        state.loadingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== TOGGLE INSTALLMENTS ==========
      .addCase(toggleInstallmentsThunk.pending, (state) => {
        state.savingConfig = true;
        state.configError = null;
      })
      .addCase(toggleInstallmentsThunk.fulfilled, (state, action) => {
        state.savingConfig = false;
        const { branchCode, enabled } = action.payload;
        if (state.config) {
          state.config.enabled = enabled;
        }
        if (state.configByBranch[branchCode]) {
          state.configByBranch[branchCode].enabled = enabled;
        }
      })
      .addCase(toggleInstallmentsThunk.rejected, (state, action) => {
        state.savingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== UPDATE INTEREST CONFIG ==========
      .addCase(updateInterestConfigThunk.pending, (state) => {
        state.savingConfig = true;
        state.configError = null;
      })
      .addCase(updateInterestConfigThunk.fulfilled, (state, action) => {
        state.savingConfig = false;
        const { branchCode, data } = action.payload;
        if (state.config) {
          state.config.interestConfig = data.data;
        }
        if (state.configByBranch[branchCode]) {
          state.configByBranch[branchCode].interestConfig = data.data;
        }
      })
      .addCase(updateInterestConfigThunk.rejected, (state, action) => {
        state.savingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== UPDATE PAYMENT RULES ==========
      .addCase(updatePaymentRulesThunk.pending, (state) => {
        state.savingConfig = true;
        state.configError = null;
      })
      .addCase(updatePaymentRulesThunk.fulfilled, (state, action) => {
        state.savingConfig = false;
        const { branchCode, data } = action.payload;
        if (state.config) {
          state.config.paymentRules = data.data;
        }
        if (state.configByBranch[branchCode]) {
          state.configByBranch[branchCode].paymentRules = data.data;
        }
      })
      .addCase(updatePaymentRulesThunk.rejected, (state, action) => {
        state.savingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== UPDATE PAUSE POLICY ==========
      .addCase(updatePausePolicyThunk.pending, (state) => {
        state.savingConfig = true;
        state.configError = null;
      })
      .addCase(updatePausePolicyThunk.fulfilled, (state, action) => {
        state.savingConfig = false;
        const { branchCode, data } = action.payload;
        if (state.config) {
          state.config.pausePolicy = data.data;
        }
        if (state.configByBranch[branchCode]) {
          state.configByBranch[branchCode].pausePolicy = data.data;
        }
      })
      .addCase(updatePausePolicyThunk.rejected, (state, action) => {
        state.savingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== UPDATE KYC REQUIREMENTS ==========
      .addCase(updateKYCRequirementsThunk.pending, (state) => {
        state.savingConfig = true;
        state.configError = null;
      })
      .addCase(updateKYCRequirementsThunk.fulfilled, (state, action) => {
        state.savingConfig = false;
        const { branchCode, data } = action.payload;
        if (state.config) {
          state.config.kycRequirements = data.data;
        }
        if (state.configByBranch[branchCode]) {
          state.configByBranch[branchCode].kycRequirements = data.data;
        }
      })
      .addCase(updateKYCRequirementsThunk.rejected, (state, action) => {
        state.savingConfig = false;
        state.configError = action.payload;
        state.error = action.payload;
      })

      // ========== GET PLANS ==========
      .addCase(getInstallmentPlansThunk.pending, (state) => {
        state.loadingPlans = true;
        state.planError = null;
      })
      .addCase(getInstallmentPlansThunk.fulfilled, (state, action) => {
        state.loadingPlans = false;
        const { branchCode, data } = action.payload;
        state.plans = data.data?.plans || [];
        state.plansByBranch[branchCode] = data.data?.plans || [];
      })
      .addCase(getInstallmentPlansThunk.rejected, (state, action) => {
        state.loadingPlans = false;
        state.planError = action.payload;
        state.error = action.payload;
      })

      // ========== CREATE PLAN ==========
      .addCase(createInstallmentPlanThunk.pending, (state) => {
        state.savingPlan = true;
        state.planError = null;
        state.validationErrors = [];
      })
      .addCase(createInstallmentPlanThunk.fulfilled, (state, action) => {
        state.savingPlan = false;
        const { branchCode, data } = action.payload;
        const newPlan = data.data;

        state.plans.push(newPlan);
        if (state.plansByBranch[branchCode]) {
          state.plansByBranch[branchCode].push(newPlan);
        } else {
          state.plansByBranch[branchCode] = [newPlan];
        }
        state.isEditingPlan = false;
      })
      .addCase(createInstallmentPlanThunk.rejected, (state, action) => {
        state.savingPlan = false;
        state.planError = action.payload?.message || action.payload;
        state.validationErrors = action.payload?.details || [];
        state.error = action.payload?.message || action.payload;
      })

      // ========== UPDATE PLAN ==========
      .addCase(updateInstallmentPlanThunk.pending, (state) => {
        state.savingPlan = true;
        state.planError = null;
      })
      .addCase(updateInstallmentPlanThunk.fulfilled, (state, action) => {
        state.savingPlan = false;
        const { branchCode, planId, data } = action.payload;
        const updatedPlan = data.data;

        // Update in plans array
        const planIndex = state.plans.findIndex(
          (p) => p._id.toString() === planId
        );
        if (planIndex !== -1) {
          state.plans[planIndex] = updatedPlan;
        }

        // Update in plansByBranch
        if (state.plansByBranch[branchCode]) {
          const branchPlanIndex = state.plansByBranch[branchCode].findIndex(
            (p) => p._id.toString() === planId
          );
          if (branchPlanIndex !== -1) {
            state.plansByBranch[branchCode][branchPlanIndex] = updatedPlan;
          }
        }

        // Update selected plan if it matches
        if (state.selectedPlan?._id.toString() === planId) {
          state.selectedPlan = updatedPlan;
        }

        state.isEditingPlan = false;
      })
      .addCase(updateInstallmentPlanThunk.rejected, (state, action) => {
        state.savingPlan = false;
        state.planError = action.payload;
        state.error = action.payload;
      })

      // ========== DELETE PLAN ==========
      .addCase(deleteInstallmentPlanThunk.pending, (state) => {
        state.savingPlan = true;
        state.planError = null;
      })
      .addCase(deleteInstallmentPlanThunk.fulfilled, (state, action) => {
        state.savingPlan = false;
        const { branchCode, planId, hardDelete } = action.payload;

        if (hardDelete) {
          // Remove from plans array
          state.plans = state.plans.filter((p) => p._id.toString() !== planId);

          // Remove from plansByBranch
          if (state.plansByBranch[branchCode]) {
            state.plansByBranch[branchCode] = state.plansByBranch[
              branchCode
            ].filter((p) => p._id.toString() !== planId);
          }
        } else {
          // Soft delete - just mark as inactive
          const planIndex = state.plans.findIndex(
            (p) => p._id.toString() === planId
          );
          if (planIndex !== -1) {
            state.plans[planIndex].isActive = false;
          }

          if (state.plansByBranch[branchCode]) {
            const branchPlanIndex = state.plansByBranch[branchCode].findIndex(
              (p) => p._id.toString() === planId
            );
            if (branchPlanIndex !== -1) {
              state.plansByBranch[branchCode][branchPlanIndex].isActive = false;
            }
          }
        }

        // Clear selected plan if it was deleted
        if (state.selectedPlan?._id.toString() === planId) {
          state.selectedPlan = null;
        }
      })
      .addCase(deleteInstallmentPlanThunk.rejected, (state, action) => {
        state.savingPlan = false;
        state.planError = action.payload?.message || action.payload;
        state.error = action.payload?.message || action.payload;
      })

      // ========== GET ANALYTICS ==========
      .addCase(getInstallmentAnalyticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
        state.analyticsError = null;
      })
      .addCase(getInstallmentAnalyticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        const { branchCode, data } = action.payload;
        state.analytics = data.data;
        state.analyticsByBranch[branchCode] = data.data;
      })
      .addCase(getInstallmentAnalyticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload;
        state.error = action.payload;
      });
  },
});

export const { setCurrentBranchCode, clearCurrentBranch } =
  branchInstallmentSlice.actions;

export default branchInstallmentSlice.reducer;
